import { PopoverPosition, Spinner, SpinnerSize } from '@blueprintjs/core';
import * as React from 'react';
import { ItemSelector } from 'vet-bones/components';

import { TEST_IDS, getModelItemTestId } from 'src/constants/testIds';
import { databaseSelector } from 'src/selectors/database';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { isDatabaseRefreshedSelector } from 'src/selectors/isRefreshingDatabase';
import { isRefreshingDatabasesSelector } from 'src/selectors/isRefreshingDatabases';
import { ModelSelectItem } from 'src/selectors/modelItems';
import * as copy from 'src/templates/copy';
import { icons } from 'src/templates/icons';
import { colors } from 'src/templates/styles/colors';
import { stopPropagation } from 'src/utils/handlers/stopPropagation';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export type ModelSelectorProps = {
  databaseId: string;
  disabled?: boolean;
  modelItems: ModelSelectItem[];
  popoverPosition?: PopoverPosition;
  selectedModel: ModelSelectItem | null;
  setSelectedModel: (item: ModelSelectItem | null) => void;
};

/**
 * (single-item) selector for models in a database
 */
export const ModelSelector: React.VFC<ModelSelectorProps> = ({
  databaseId,
  disabled,
  modelItems,
  popoverPosition,
  selectedModel,
  setSelectedModel,
}) => {
  const isRefreshingDatabases = useReduxSelector(isRefreshingDatabasesSelector);
  const isDatabaseRefreshed = useReduxSelector(
    isDatabaseRefreshedSelector(databaseId)
  );
  const database = useReduxSelector(databaseSelector(databaseId));
  const isConnected = useReduxSelector(isConnectedSelector);

  const isSelectorDisabled =
    !isConnected ||
    isRefreshingDatabases ||
    (databaseId && !isDatabaseRefreshed) ||
    disabled;

  const handleSelectModel = (
    item: ModelSelectItem | null,
    evt?: React.MouseEvent
  ) => {
    stopPropagation(evt);
    if (item) {
      setSelectedModel(item);
    } else {
      setSelectedModel(null);
    }
  };

  return (
    <ItemSelector.Single<ModelSelectItem, ModelSelectItem>
      buttonIcon={icons.model}
      buttonIconColor={selectedModel ? colors.darkIcon : undefined}
      buttonRightIcon={
        isRefreshingDatabases || (database && !isDatabaseRefreshed) ? (
          <Spinner size={SpinnerSize.SMALL} />
        ) : undefined
      }
      buttonText={
        selectedModel?.text || copy.components.cards.model.placeholder
      }
      disabled={isSelectorDisabled}
      filterText={copy.components.defaults.filter}
      items={modelItems}
      itemTestId={getModelItemTestId}
      onButtonClick={stopPropagation}
      onClear={stopPropagation}
      onSelect={handleSelectModel}
      popoverProps={{
        backdropProps: { onClick: stopPropagation },
        position: popoverPosition,
      }}
      selected={selectedModel}
      testId={TEST_IDS.model.selector}
    />
  );
};
